import * as React from 'react';
import { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeDownIcon from '@mui/icons-material/VolumeDown';
import VolumeMuteIcon from '@mui/icons-material/VolumeMute';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import RepeatIcon from '@mui/icons-material/Repeat';
import RepeatOneIcon from '@mui/icons-material/RepeatOne';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import Slider from '@mui/material/Slider';
import useMediaQuery from '@mui/material/useMediaQuery';
import { getImageUrl, shouldUseCrossOrigin } from './constants/routes';
import { AppRoutes } from './constants/routes';
import { useMusicPlayer } from './contexts/MusicPlayerContext';
import PlayHistory from './components/PlayHistory';
import LikeButton from './components/LikeButton';
import SaveButton from './components/SaveButton';
import { useAuthProvider } from './utils/auth';
import CreditWarningInPlayer from './components/CreditWarningInPlayer';
import PlayPauseButton from './components/player/PlayPauseButton';
import { QualityMenuButton, QualitySuggestion } from './components/player/QualityControl';
import { useNavigate } from 'react-router-dom';
import { useEffect, useRef } from 'react';

// ScrollingText component for mobile player
const ScrollingText = ({ text, variant = 'body2', sx = {} }) => {
  const textRef = useRef(null);
  const [shouldScroll, setShouldScroll] = useState(false);

  useEffect(() => {
    if (textRef.current) {
      const element = textRef.current;
      const isOverflowing = element.scrollWidth > element.clientWidth;
      setShouldScroll(isOverflowing);
    }
  }, [text]);

  return (
    <Typography
      ref={textRef}
      variant={variant}
      sx={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        ...(shouldScroll && {
          animation: 'scrollText 8s linear infinite',
          '&:hover': {
            animationPlayState: 'paused'
          }
        }),
        ...sx
      }}
    >
      {text}
    </Typography>
  );
};

export default function MusicPlayer() {
  const { userType, isListening, user } = useAuthProvider();
  const navigate = useNavigate();
  const {
    currentTrack,
    isPlaying,
    progress,
    duration,
    volume,
    lastPlayedState,
    isLoading,
    isBuffering,
    isShuffleOn,
    repeatMode,
    playPause,
    next,
    previous,
    seekTo,
    setVolume,
    resumeFromLastPosition,
    toggleShuffle,
    toggleRepeat,
  } = useMusicPlayer();

  const theme = useTheme();
  const [playHistoryOpen, setPlayHistoryOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Check if current track is owned by the creator (basic frontend check for UX)
  // Note: Backend handles all free access cases (creators, act members, label owners, credited users)
  // This frontend check is just for UX optimization - backend will allow playback regardless
  // Support both nested primary_act_detail (new) and primary_act.creator (old) formats
  // The creator may be a bare uuid (public track data) or an object
  const creatorId = (creator) => (creator && typeof creator === 'object' ? creator.uuid || creator.id : creator);
  const isOwnTrack = Boolean(currentTrack && user?.uuid && [
    currentTrack.primary_act_detail?.creator,
    currentTrack.primary_act?.creator,
  ].some((creator) => creator && creatorId(creator) === user.uuid));

  // Don't show music player for creators, labels, or publishers unless they're in listening mode
  // EXCEPTION: Show player if creator is playing their own track
  // Note: Backend allows free playback for act members, label owners, and credited users too
  if (!isListening && !(userType === 'creator' && isOwnTrack)) {
    return null;
  }

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleProgressChange = (event, newValue) => {
    console.log('🎵 Progress bar clicked - newValue:', newValue, 'isResumeMode:', isResumeMode);
    
    if (isResumeMode) {
      // In resume mode, clicking the progress bar should resume from that position
      console.log('🎵 Resuming from clicked position:', newValue);
      resumeFromLastPosition(newValue);
    } else {
      // Normal seeking when audio is active
      seekTo(newValue);
    }
  };

  const handleVolumeChange = (event, newValue) => {
    setVolume(newValue);
  };

  const getVolumeIcon = () => {
    if (volume === 0) return <VolumeMuteIcon />;
    if (volume < 0.5) return <VolumeDownIcon />;
    return <VolumeUpIcon />;
  };

  // Show last played track if no current track
  const displayTrack = currentTrack || lastPlayedState?.track;
  const isResumeMode = !currentTrack && lastPlayedState?.track;
  
  // Check if user has never played music (no current track and no play history)
  const hasNeverPlayedMusic = !currentTrack && !lastPlayedState?.track;
  
  // Create placeholder track data for new users
  const placeholderTrack = {
    name: 'Your Favorite Song',
    act_name: 'Your Favorite Artist',
    image: null, // Will use placeholder icon
    id: null
  };
  
  // Use placeholder data if user has never played music
  const finalDisplayTrack = hasNeverPlayedMusic ? placeholderTrack : displayTrack;

  // Mobile Music Player
  if (isMobile) {
    return (
      <>
        <style>
          {`
            @keyframes scrollText {
              0% { transform: translateX(0); }
              50% { transform: translateX(-50%); }
              100% { transform: translateX(0); }
            }
            .scrolling-text {
              animation: scrollText 8s linear infinite;
            }
          `}
        </style>
        <Box sx={{ position: 'relative' }}>
          <CreditWarningInPlayer />
          <Card sx={{ 
            display: 'flex', 
            position: 'fixed',
            bottom: 56, // Above mobile navigation
            left: 0,
            right: 0,
            zIndex: 1000,
            borderRadius: 0,
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            maxWidth: '100vw',
            overflow: 'hidden'
          }}>
          <CardContent sx={{ flex: '1 0 auto', py: 1.5, px: { xs: 1.5, sm: 2 }, display: 'flex', alignItems: 'center', minHeight: '80px', overflow: 'hidden', maxWidth: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, width: '100%', minWidth: 0, overflow: 'hidden' }}>
              {/* Track Artwork - Clickable to go to Now Playing */}
              <Box
                onClick={() => navigate(AppRoutes.NowPlaying)}
                sx={{
                  cursor: 'pointer',
                  flexShrink: 0
                }}
              >
                {hasNeverPlayedMusic ? (
                  <Box
                    sx={{ 
                      width: { xs: 44, sm: 48 }, 
                      height: { xs: 44, sm: 48 }, 
                      borderRadius: 1.5,
                      backgroundColor: 'grey.800',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: 1,
                      borderColor: 'grey.600',
                      flexShrink: 0
                    }}
                  >
                    <MusicNoteIcon sx={{ color: 'grey.400', fontSize: { xs: 18, sm: 20 } }} />
                  </Box>
                ) : (
                  <CardMedia
                    component="img"
                    sx={{ 
                      width: { xs: 44, sm: 48 }, 
                      height: { xs: 44, sm: 48 }, 
                      borderRadius: 1.5,
                      flexShrink: 0
                    }}
                    image={finalDisplayTrack?.image ? getImageUrl(finalDisplayTrack.image) : '/default-album-art.jpg'}
                    alt={finalDisplayTrack?.name || 'Track'}
                    crossOrigin={finalDisplayTrack?.image && shouldUseCrossOrigin(getImageUrl(finalDisplayTrack.image)) ? "anonymous" : undefined}
                  />
                )}
              </Box>
              
              {/* Track Info - Clickable to go to Now Playing */}
              <Box 
                onClick={() => navigate(AppRoutes.NowPlaying)}
                sx={{ 
                  minWidth: 0, 
                  flex: 1, 
                  py: 0.5,
                  cursor: 'pointer',
                  overflow: 'hidden',
                  maxWidth: '100%',
                  '&:hover': {
                    opacity: 0.8
                  }
                }}
              >
                <ScrollingText
                  text={finalDisplayTrack?.name || 'No track playing'}
                  variant="subtitle2"
                  sx={{ 
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    lineHeight: 1.3,
                    mb: 0.5,
                    maxWidth: '100%',
                    overflow: 'hidden'
                  }}
                />
                {(finalDisplayTrack?.act_name || isResumeMode) && (
                  <ScrollingText
                    text={finalDisplayTrack?.act_name ? finalDisplayTrack.act_name : 'Tap to resume'}
                    variant="body2"
                    sx={{
                      fontSize: '0.8rem',
                      fontWeight: 'normal',
                      lineHeight: 1.2,
                      color: 'text.secondary',
                      maxWidth: '100%',
                      overflow: 'hidden'
                    }}
                  />
                )}
              </Box>

              {/* Mobile Controls - Previous, Play/Pause, and Next */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, flexShrink: 0, minWidth: 'fit-content' }}>
                <IconButton 
                  aria-label="previous" 
                  onClick={previous}
                  size="small"
                  sx={{ 
                    height: { xs: 32, sm: 36 }, 
                    width: { xs: 32, sm: 36 },
                    minWidth: { xs: 32, sm: 36 },
                    color: 'text.secondary'
                  }}
                >
                  <SkipPreviousIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                </IconButton>
                <PlayPauseButton
                  isPlaying={isPlaying}
                  busy={isLoading || isBuffering}
                  onClick={isResumeMode ? resumeFromLastPosition : playPause}
                  disabled={isLoading}
                  size={40}
                  iconSize={{ xs: 20, sm: 22 }}
                  sx={{ height: { xs: 36, sm: 40 }, width: { xs: 36, sm: 40 }, minWidth: { xs: 36, sm: 40 }, boxShadow: 1 }}
                />
                <IconButton 
                  aria-label="next" 
                  onClick={next}
                  size="small"
                  sx={{ 
                    height: { xs: 32, sm: 36 }, 
                    width: { xs: 32, sm: 36 },
                    minWidth: { xs: 32, sm: 36 },
                    color: 'text.secondary'
                  }}
                >
                  <SkipNextIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                </IconButton>
                <QualityMenuButton sx={{ ml: 0.5 }} />
              </Box>
            </Box>
          </CardContent>
        </Card>
        </Box>
        
        <QualitySuggestion />

        {/* Play History Dialog */}
        <PlayHistory 
          open={playHistoryOpen} 
          onClose={() => setPlayHistoryOpen(false)} 
        />
      </>
    );
  }

  // Desktop Music Player
  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <CreditWarningInPlayer />
        <Card sx={{ 
          display: 'flex', 
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          borderRadius: 0,
          borderTop: 1,
          borderColor: 'divider'
        }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        <CardContent sx={{ flex: '1 0 auto', py: 2, display: 'flex', alignItems: 'center', minHeight: '80px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            {/* Track Info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200, maxWidth: 400, flexShrink: 1 }}>
              {hasNeverPlayedMusic ? (
                <Box
                  onClick={() => navigate(AppRoutes.NowPlaying)}
                  sx={{ 
                    width: 56, 
                    height: 56, 
                    borderRadius: 1,
                    backgroundColor: 'grey.800',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 1,
                    borderColor: 'grey.600',
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 0.8
                    }
                  }}
                >
                  <MusicNoteIcon sx={{ color: 'grey.400', fontSize: 28 }} />
                </Box>
              ) : (
                <CardMedia
                  component="img"
                  onClick={() => navigate(AppRoutes.NowPlaying)}
                  sx={{ 
                    width: 56, 
                    height: 56, 
                    borderRadius: 1,
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 0.8
                    }
                  }}
                  image={finalDisplayTrack?.image ? getImageUrl(finalDisplayTrack.image) : '/default-album-art.jpg'}
                  alt={finalDisplayTrack?.name || 'Track'}
                  crossOrigin={finalDisplayTrack?.image && shouldUseCrossOrigin(getImageUrl(finalDisplayTrack.image)) ? "anonymous" : undefined}
                />
              )}
              <Box sx={{ minWidth: 120, flex: 1 }}>
                <Box
                  onClick={() => {
                    if (finalDisplayTrack?.slug || finalDisplayTrack?.id) {
                      navigate(AppRoutes.SinglePage.replace(':slug', finalDisplayTrack?.slug || finalDisplayTrack.id));
                    } else {
                      setPlayHistoryOpen(true);
                    }
                  }}
                  sx={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  <Typography 
                    component="div" 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 'medium',
                      fontSize: '1rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {finalDisplayTrack?.name || 'No track playing'}
                  </Typography>
                </Box>
                <Box
                  onClick={() => {
                    if (finalDisplayTrack?.act_slug || finalDisplayTrack?.acts) {
                      navigate(AppRoutes.ActPage.replace(':slug', finalDisplayTrack?.act_slug || finalDisplayTrack.acts));
                    }
                  }}
                  sx={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: finalDisplayTrack?.acts ? 'pointer' : 'default'
                  }}
                >
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    component="div"
                    sx={{
                      fontSize: '0.875rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {finalDisplayTrack?.primary_acts_display || finalDisplayTrack?.act_name || (isResumeMode ? 'Click to resume' : 'Unknown Artist')}
                    {finalDisplayTrack?.featured_acts_display && ` ${finalDisplayTrack.featured_acts_display}`}
                  </Typography>
                </Box>
              </Box>
              
              {/* Like and Save Buttons for current track */}
              {finalDisplayTrack?.id && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5, 
                  flexShrink: 0,
                  ml: 1
                }}>
                  <LikeButton
                    contentType="track"
                    objectId={finalDisplayTrack.id}
                    size="small"
                    sx={{ 
                      color: 'text.secondary',
                      minWidth: '2rem',
                      minHeight: '2rem',
                      width: '2rem',
                      height: '2rem',
                      '&:hover': { 
                        color: 'error.main',
                        bgcolor: 'action.hover' 
                      }
                    }}
                  />
                  <SaveButton
                    contentType="track"
                    objectId={finalDisplayTrack.id}
                    size="small"
                    sx={{ 
                      color: 'text.secondary',
                      minWidth: '2rem',
                      minHeight: '2rem',
                      width: '2rem',
                      height: '2rem',
                      '&:hover': { 
                        color: 'primary.main',
                        bgcolor: 'action.hover' 
                      }
                    }}
                  />
                </Box>
              )}
            </Box>

            {/* Playback Controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, ml: 2 }}>
              <IconButton 
                aria-label="shuffle" 
                onClick={toggleShuffle}
                sx={{ 
                  color: isShuffleOn ? 'primary.main' : 'text.secondary',
                  '&:hover': { color: 'primary.main' }
                }}
              >
                <ShuffleIcon />
              </IconButton>
              <IconButton aria-label="previous" onClick={previous}>
                {theme.direction === 'rtl' ? <SkipNextIcon /> : <SkipPreviousIcon />}
              </IconButton>
              <PlayPauseButton
                isPlaying={isPlaying}
                busy={isLoading || isBuffering}
                onClick={isResumeMode ? () => resumeFromLastPosition() : playPause}
                disabled={isLoading}
                size={48}
              />
              <IconButton aria-label="next" onClick={next}>
                {theme.direction === 'rtl' ? <SkipPreviousIcon /> : <SkipNextIcon />}
              </IconButton>
              <IconButton 
                aria-label="repeat" 
                onClick={toggleRepeat}
                sx={{ 
                  color: repeatMode !== 'off' ? 'primary.main' : 'text.secondary',
                  '&:hover': { color: 'primary.main' }
                }}
              >
                {repeatMode === 'one' ? <RepeatOneIcon /> : <RepeatIcon />}
              </IconButton>
            </Box>

            {/* Progress Bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, mx: 2, minWidth: 150 }}>
              <Typography variant="caption" sx={{ minWidth: 40 }}>
                {formatTime(isResumeMode ? lastPlayedState?.progress : progress)}
              </Typography>
              <Slider
                size="small"
                value={isResumeMode ? lastPlayedState?.progress || 0 : progress}
                onChange={handleProgressChange}
                max={duration}
                aria-label="Seek"
                inputProps={{ 'aria-label': 'Seek' }}
                sx={{ 
                  color: 'primary.main',
                  '& .MuiSlider-thumb': {
                    width: 12,
                    height: 12,
                  }
                }}
              />
              <Typography variant="caption" sx={{ minWidth: 40 }}>
                {formatTime(duration)}
              </Typography>
            </Box>

            {/* Volume Control */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, minWidth: 150 }}>
              <QualityMenuButton sx={{ mr: 0.5 }} />
              <IconButton size="small" aria-label="volume">
                {getVolumeIcon()}
              </IconButton>
              <Slider
                size="small"
                value={volume}
                onChange={handleVolumeChange}
                max={1}
                step={0.01}
                aria-label="Volume"
                inputProps={{ 'aria-label': 'Volume' }}
                sx={{ 
                  color: 'primary.main',
                  '& .MuiSlider-thumb': {
                    width: 12,
                    height: 12,
                  }
                }}
              />
            </Box>
          </Box>
        </CardContent>
      </Box>
      </Card>
      </Box>
      
      <QualitySuggestion />

      {/* Play History Dialog */}
      <PlayHistory
        open={playHistoryOpen}
        onClose={() => setPlayHistoryOpen(false)}
      />

      {/* Credit Warning Dialog now rendered globally in App.js - this
          component is conditionally unmounted (see early "return null" above
          and App.js's hasStreamingAccess gate), so it can't reliably show the
          "please log in / add credit" prompt for every caller of
          showCreditWarning (e.g. anonymous visitors). */}
    </>
  );
}
